import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import {
  getHotlineLiveSheets,
  HotlineLiveSheetConfig,
  saveHotlineLiveSheet,
} from "../../services/hotlineQueueApi";

const SHEETS = [
  {
    id: "apna",
    title: "Apna",
    detail: "Candidates from the Apna sheet are passed into Hotline when a manager turns this source on.",
  },
  {
    id: "jobhe",
    title: "Jobhe",
    detail: "Candidates from the Jobhe sheet are passed into Hotline when a manager turns this source on.",
  },
  {
    id: "workIndia",
    title: "Work India",
    detail: "Candidates from the Work India sheet are passed into Hotline when a manager turns this source on.",
  },
] as const;

type SheetDraft = {
  url: string;
  live: boolean;
  tabName: string;
  candidateNameColumn: string;
  numberColumn: string;
  jobIdColumn: string;
  status: string;
};

const emptyDraft = (): SheetDraft => ({
  url: "",
  live: true,
  tabName: "",
  candidateNameColumn: "",
  numberColumn: "",
  jobIdColumn: "",
  status: "",
});

function errorMessage(error: unknown) {
  const body = (error as { response?: { data?: { message?: string } } })?.response?.data;
  return body?.message || "The sheet could not be saved.";
}

function statusText(sheet: HotlineLiveSheetConfig) {
  if (sheet.lastError) return sheet.lastError;
  if (!sheet.lastReadAt) return sheet.live ? "Waiting for the first read." : "";
  const stats = sheet.lastStats;
  const when = new Date(sheet.lastReadAt).toLocaleString();
  if (!stats) return `Last read ${when}.`;
  return `Last read ${when}. ${stats.added} new, ${stats.passed} passed to a recruiter, ${stats.waiting} waiting.`;
}

const LiveSheetsPage: React.FC = () => {
  const [drafts, setDrafts] = useState<Record<string, SheetDraft>>({});
  const [savingId, setSavingId] = useState("");

  useEffect(() => {
    let cancelled = false;
    getHotlineLiveSheets()
      .then((sheets) => {
        if (cancelled) return;
        setDrafts(
          Object.fromEntries(
            sheets.map((sheet) => [
              sheet.source,
              {
                url: sheet.spreadsheetUrl,
                live: sheet.live,
                tabName: sheet.tabName,
                candidateNameColumn: sheet.candidateNameColumn,
                numberColumn: sheet.numberColumn,
                jobIdColumn: sheet.jobIdColumn,
                status: statusText(sheet),
              },
            ]),
          ),
        );
      })
      .catch((error) => {
        if (!cancelled) toast.error(errorMessage(error));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const draftFor = (id: string) => drafts[id] ?? emptyDraft();

  const update = (id: string, patch: Partial<SheetDraft>) => {
    setDrafts((current) => {
      const previous = current[id] ?? emptyDraft();
      return { ...current, [id]: { ...previous, ...patch } };
    });
  };

  const save = async (title: string, id: string) => {
    const draft = draftFor(id);
    if (draft.live && !draft.url.trim()) {
      toast.error(`Add the ${title} sheet link first.`);
      return;
    }
    if (draft.live && !draft.tabName.trim()) {
      toast.error(`Add the ${title} sheet tab name.`);
      return;
    }
    if (
      draft.live &&
      (!draft.candidateNameColumn.trim() || !draft.numberColumn.trim() || !draft.jobIdColumn.trim())
    ) {
      toast.error(`Match the candidate name, number, and job ID columns for ${title}.`);
      return;
    }
    setSavingId(id);
    try {
      const saved = await saveHotlineLiveSheet(id, {
        spreadsheetUrl: draft.url.trim(),
        tabName: draft.tabName.trim(),
        candidateNameColumn: draft.candidateNameColumn.trim(),
        numberColumn: draft.numberColumn.trim(),
        jobIdColumn: draft.jobIdColumn.trim(),
        live: draft.live,
      });
      update(id, { status: statusText(saved) });
      toast.success(
        saved.live ? `${title} is live. New rows are read from the sheet.` : `${title} is off.`,
      );
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setSavingId("");
    }
  };

  return (
    <div className="space-y-6 p-4 md:p-6">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">Live sheets</h1>
        <p className="mt-1 max-w-2xl text-sm text-slate-500">
          An admin keeps the Apna, Jobhe, and Work India sheets. Paste each live sheet link here. Managers then choose which branch receives the rows.
        </p>
      </div>

      <div className="grid gap-6 xl:grid-cols-3">
        {SHEETS.map((sheet) => {
          const draft = draftFor(sheet.id);
          return (
            <section key={sheet.id} className="rounded-xl border border-slate-200 bg-white p-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h2 className="text-sm font-semibold text-slate-800">{sheet.title}</h2>
                  <p className="mt-1 text-xs text-slate-500">{sheet.detail}</p>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={draft.live}
                  onClick={() => update(sheet.id, { live: !draft.live })}
                  className="flex items-center gap-2 text-sm font-medium text-slate-800"
                >
                  <span>{draft.live ? "Live" : "Off"}</span>
                  <span
                    className={`relative h-6 w-11 rounded-full ${draft.live ? "bg-[#EC6E51]" : "bg-slate-300"}`}
                  >
                    <span
                      className={`absolute top-0.5 h-5 w-5 rounded-full bg-white ${draft.live ? "left-5" : "left-0.5"}`}
                    />
                  </span>
                </button>
              </div>

              <label className="mt-4 block text-sm">
                <span className="mb-1 block text-xs font-medium text-slate-500">Sheet link</span>
                <input
                  value={draft.url}
                  onChange={(event) => update(sheet.id, { url: event.target.value })}
                  placeholder="https://"
                  className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-[#EC6E51]"
                />
              </label>
              <label className="mt-4 block text-sm">
                <span className="mb-1 block text-xs font-medium text-slate-500">Sheet tab name</span>
                <input
                  value={draft.tabName}
                  onChange={(event) => update(sheet.id, { tabName: event.target.value })}
                  placeholder="Tab name in the sheet"
                  className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-[#EC6E51]"
                />
              </label>
              <label className="mt-4 block text-sm">
                <span className="mb-1 block text-xs font-medium text-slate-500">Candidate name column</span>
                <input
                  value={draft.candidateNameColumn}
                  onChange={(event) => update(sheet.id, { candidateNameColumn: event.target.value })}
                  placeholder="Header in the sheet for the name"
                  className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-[#EC6E51]"
                />
              </label>
              <label className="mt-4 block text-sm">
                <span className="mb-1 block text-xs font-medium text-slate-500">Number column</span>
                <input
                  value={draft.numberColumn}
                  onChange={(event) => update(sheet.id, { numberColumn: event.target.value })}
                  placeholder="Header in the sheet for the mobile"
                  className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-[#EC6E51]"
                />
              </label>
              <label className="mt-4 block text-sm">
                <span className="mb-1 block text-xs font-medium text-slate-500">Job ID column</span>
                <input
                  value={draft.jobIdColumn}
                  onChange={(event) => update(sheet.id, { jobIdColumn: event.target.value })}
                  placeholder="Header in the sheet for the job ID"
                  className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-[#EC6E51]"
                />
              </label>
              {draft.status && <p className="mt-3 text-xs text-slate-500">{draft.status}</p>}
              <button
                type="button"
                disabled={savingId === sheet.id}
                onClick={() => void save(sheet.title, sheet.id)}
                className="mt-4 rounded-lg bg-[#EC6E51] px-4 py-2 text-sm font-medium text-white disabled:opacity-60"
              >
                {savingId === sheet.id ? "Saving…" : "Save"}
              </button>
            </section>
          );
        })}
      </div>
    </div>
  );
};

export default LiveSheetsPage;
